import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    FileDown, RefreshCw, AlertTriangle, ShieldAlert,
    Globe, Rss, ExternalLink, ShieldCheck, Zap,
    AlertCircle, Activity, User, ArrowRight, Shield,
    CheckCircle2, Clock, Filter, Search, X, ChevronDown,
    Layers, Server, Terminal, Lock, Flame, Cpu,
    Crosshair, Compass, ChevronRight, Hash, Play, Eye
} from "lucide-react";
import { backendURL } from "../config";

// --- Color & Severity Styling Helpers ---
const SEVERITY_THEMES = {
    CRITICAL: {
        bg: "bg-red-500/10 border-red-500/30 text-red-400",
        badge: "bg-red-500/20 text-red-400 border border-red-500/30",
        dot: "bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.8)]",
        glow: "bg-red-500"
    },
    HIGH: {
        bg: "bg-orange-500/10 border-orange-500/30 text-orange-400",
        badge: "bg-orange-500/20 text-orange-400 border border-orange-500/30",
        dot: "bg-orange-500 shadow-[0_0_10px_rgba(249,115,22,0.8)]",
        glow: "bg-orange-500"
    },
    MEDIUM: {
        bg: "bg-amber-500/10 border-amber-500/30 text-amber-400",
        badge: "bg-amber-500/20 text-amber-400 border border-amber-500/30",
        dot: "bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.8)]",
        glow: "bg-amber-500"
    },
    LOW: {
        bg: "bg-blue-500/10 border-blue-500/30 text-blue-400",
        badge: "bg-blue-500/20 text-blue-400 border border-blue-500/30",
        dot: "bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.8)]",
        glow: "bg-blue-500"
    }
};

const SOURCE_COLORS = {
    BleepingComputer: "text-sky-400 bg-sky-500/10 border-sky-500/20",
    "DataBreaches.Net": "text-rose-400 bg-rose-500/10 border-rose-500/20",
    "The Hacker News": "text-amber-400 bg-amber-500/10 border-amber-500/20",
    "Krebs on Security": "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    Threatpost: "text-purple-400 bg-purple-500/10 border-purple-500/20",
    darkreading: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20",
    Default: "text-blue-400 bg-blue-500/10 border-blue-500/20"
};

// Stat Block Component
const StatCard = ({ title, value, sub, icon: Icon, color = "blue", badge = null }) => {
    const colorMap = {
        blue: "bg-blue-500/10 border-blue-500/20 text-blue-400",
        red: "bg-red-500/10 border-red-500/20 text-red-400",
        orange: "bg-orange-500/10 border-orange-500/20 text-orange-400",
        emerald: "bg-emerald-500/10 border-emerald-500/20 text-emerald-400",
        purple: "bg-purple-500/10 border-purple-500/20 text-purple-400",
        cyan: "bg-cyan-500/10 border-cyan-500/20 text-cyan-400"
    };

    return (
        <div className="bg-[#0F172A]/90 backdrop-blur-xl border border-white/[0.08] p-5 rounded-2xl relative overflow-hidden group shadow-xl transition-all duration-300 hover:border-white/[0.15]">
            <div className={`absolute top-0 right-0 w-28 h-28 ${colorMap[color].split(" ")[0]} blur-3xl -mr-12 -mt-12 transition-all duration-500 group-hover:opacity-80 opacity-40`} />

            <div className="flex items-center justify-between mb-3 relative z-10">
                <div className={`p-2.5 rounded-xl border ${colorMap[color]}`}>
                    <Icon className="w-5 h-5" />
                </div>
                {badge && (
                    <span className="text-[9px] font-black font-mono px-2 py-0.5 rounded-md bg-[#1E293B] border border-white/[0.08] text-slate-300 uppercase tracking-wider">
                        {badge}
                    </span>
                )}
            </div>

            <div className="relative z-10">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{title}</p>
                <div className="text-2xl lg:text-3xl font-black text-slate-100 font-mono tracking-tight leading-none mb-1.5">
                    {value}
                </div>
                <p className="text-[11px] text-slate-400 font-medium truncate">{sub}</p>
            </div>
        </div>
    );
};

export default function AIThreatInsights() {
    const navigate = useNavigate();

    // Data States
    const [insights, setInsights] = useState([]);
    const [metrics, setMetrics] = useState({
        totalVectors: 0,
        criticalCount: 0,
        highCount: 0,
        mediumCount: 0,
        environmentHits: 0
    });
    const [headlines, setHeadlines] = useState([]);
    const [headlinesTotal, setHeadlinesTotal] = useState(0);
    const [headlinesPage, setHeadlinesPage] = useState(1);

    // UI & Filter States
    const [loading, setLoading] = useState(true);
    const [headlinesLoading, setHeadlinesLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [error, setError] = useState(null);

    // Filters
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedSeverity, setSelectedSeverity] = useState("all");
    const [selectedType, setSelectedType] = useState("all");

    // Inspection Modal State
    const [inspectVector, setInspectVector] = useState(null);

    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // Fetch Threat Insights
    const fetchInsights = useCallback(async (isSilent = false) => {
        if (!isSilent) setLoading(true);
        setRefreshing(true);
        setError(null);

        const p = new URLSearchParams();
        if (searchQuery.trim()) p.append("search", searchQuery.trim());
        if (selectedSeverity !== "all") p.append("severity", selectedSeverity);
        if (selectedType !== "all") p.append("type", selectedType);

        try {
            const res = await fetch(`${backendURL}/api/threat-insights?${p.toString()}`, { headers });
            if (res.ok) {
                const data = await res.json();
                setInsights(data.insights || []);
                if (data.metrics) setMetrics(data.metrics);
            } else {
                setError("Heuristic Correlation Engine Offline.");
            }
        } catch (err) {
            console.error("Threat insights fetch error:", err);
            setError("Failed to communicate with Threat Matrix Engine.");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [searchQuery, selectedSeverity, selectedType]);

    // Fetch Global Headlines (1,480+ real breach articles)
    const fetchHeadlines = useCallback(async (page = 1) => {
        setHeadlinesLoading(true);
        const p = new URLSearchParams();
        p.append("page", String(page));
        p.append("limit", "14");
        if (searchQuery.trim()) p.append("search", searchQuery.trim());

        try {
            const res = await fetch(`${backendURL}/api/darkweb/headlines?${p.toString()}`, { headers });
            if (res.ok) {
                const data = await res.json();
                setHeadlines(data.items || []);
                setHeadlinesTotal(data.total || 0);
                setHeadlinesPage(data.page || 1);
            }
        } catch (err) {
            console.error("Headlines fetch error:", err);
        } finally {
            setHeadlinesLoading(false);
        }
    }, [searchQuery]);

    useEffect(() => {
        fetchInsights();
        fetchHeadlines(1);
    }, [fetchInsights, fetchHeadlines]);

    // Auto-Refresh Interval
    useEffect(() => {
        let interval = null;
        if (autoRefresh) {
            interval = setInterval(() => {
                fetchInsights(true);
                fetchHeadlines(headlinesPage);
            }, 30000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [autoRefresh, fetchInsights, fetchHeadlines, headlinesPage]);

    const formatTimestamp = (dateStr) => {
        if (!dateStr) return "Just now";
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return dateStr;
            return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
        } catch {
            return dateStr;
        }
    };

    // Export to CSV
    const exportToCSV = () => {
        const headersList = ["Source", "Type", "Severity", "Target Asset", "CVE", "Environment Hits", "Description", "Timestamp"];
        const rows = [
            ...insights.map(i => [
                `"${i.source || 'Sentinel SIEM'}"`,
                `"${i.type}"`,
                `"${i.severity}"`,
                `"${i.targetAsset || ''}"`,
                `"${i.cve || 'N/A'}"`,
                `"${i.hitsInEnv || 0}"`,
                `"${(i.description || '').replace(/"/g, '""')}"`,
                `"${i.timestamp}"`
            ]),
            ...headlines.map(h => [
                `"${h.source}"`,
                `"Global Breach"`,
                `"${h.exposure ? 'CRITICAL' : 'INFO'}"`,
                `"Global"`,
                `"N/A"`,
                `"0"`,
                `"${(h.title || '').replace(/"/g, '""')}"`,
                `"${h.date}"`
            ])
        ];

        const csvContent = "data:text/csv;charset=utf-8,"
            + headersList.join(",") + "\n"
            + rows.map(r => r.join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.href = encodedUri;
        link.download = `AI_Threat_Matrix_Audit_${new Date().toISOString().split("T")[0]}.csv`;
        link.click();
    };

    const activeFilterCount = (selectedSeverity !== "all" ? 1 : 0) +
        (selectedType !== "all" ? 1 : 0) +
        (searchQuery.trim() ? 1 : 0);

    const clearFilters = () => {
        setSearchQuery("");
        setSelectedSeverity("all");
        setSelectedType("all");
    };

    return (
        <div className="p-4 lg:p-8 space-y-6 max-w-[1700px] mx-auto min-h-screen text-slate-100 font-sans pb-24">

            {/* ── HEADER & OPERATIONS COMMAND BAR ── */}
            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-5 bg-[#0F172A]/90 backdrop-blur-xl p-6 rounded-3xl border border-white/[0.08] shadow-2xl relative overflow-hidden">
                <div className="flex items-start sm:items-center gap-4 relative z-10">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-600/20 to-orange-600/10 border border-red-500/30 flex items-center justify-center shrink-0 shadow-[0_0_25px_rgba(239,68,68,0.2)]">
                        <ShieldAlert className="w-7 h-7 text-red-500 animate-pulse" />
                    </div>
                    <div>
                        <div className="flex flex-wrap items-center gap-3">
                            <h1 className="text-2xl lg:text-3xl font-black text-slate-100 tracking-tight uppercase">
                                AI Threat Intelligence Matrix
                            </h1>
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-red-500/20 text-red-400 border border-red-500/30 shadow-[0_0_12px_rgba(239,68,68,0.3)]">
                                Live Correlation
                            </span>
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                CTI Nodes Synced
                            </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1 flex flex-wrap items-center gap-2 font-medium">
                            <Activity className="w-3.5 h-3.5 text-blue-400" />
                            Multi-Vector Heuristic Analysis &bull; Real-time CTI Advisories &bull; Global Darknet Breach Feed
                        </p>
                    </div>
                </div>

                {/* Actions Bar */}
                <div className="flex flex-wrap items-center gap-3 relative z-10">
                    {/* Auto-Sync Toggle */}
                    <div className="flex items-center gap-2 bg-[#1E293B]/70 px-3 py-1.5 rounded-xl border border-white/[0.06]">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Auto-Sync</span>
                        <button
                            onClick={() => setAutoRefresh(!autoRefresh)}
                            className={`w-9 h-5 rounded-full transition-colors relative flex items-center p-0.5 ${autoRefresh ? "bg-blue-600" : "bg-slate-700"}`}
                        >
                            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${autoRefresh ? "translate-x-4" : "translate-x-0"}`} />
                        </button>
                    </div>

                    {/* Sync Nodes Button */}
                    <button
                        onClick={() => { fetchInsights(); fetchHeadlines(headlinesPage); }}
                        disabled={refreshing}
                        className="px-4 py-2 bg-[#1E293B]/80 hover:bg-[#1E293B] border border-white/[0.08] hover:border-white/[0.15] text-slate-200 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-all active:scale-95 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 text-blue-400 ${refreshing ? "animate-spin" : ""}`} />
                        {refreshing ? "Syncing..." : "Sync Nodes"}
                    </button>

                    {/* Export Button */}
                    <button
                        onClick={exportToCSV}
                        className="px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all shadow-[0_0_20px_rgba(59,130,246,0.3)] active:scale-95 cursor-pointer"
                    >
                        <FileDown className="w-4 h-4" />
                        Export Audit
                    </button>
                </div>
            </div>

            {/* ── 4 EXECUTIVE KPI METRICS STRIP ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    title="Correlated Vectors"
                    value={metrics.totalVectors}
                    sub={`${metrics.criticalCount} Critical · ${metrics.highCount} High Priority`}
                    icon={ShieldAlert}
                    color="red"
                    badge="HEURISTIC"
                />
                <StatCard
                    title="Global Threat Signals"
                    value={headlinesTotal ? headlinesTotal.toLocaleString() : "1,480+"}
                    sub="Verified Threat Intel Feeds"
                    icon={Globe}
                    color="cyan"
                    badge="LIVE INTEL"
                />
                <StatCard
                    title="Perimeter Signal Hits"
                    value={`${metrics.environmentHits} Matches`}
                    sub="Active Environment Signatures"
                    icon={Flame}
                    color="orange"
                    badge="TELEMETRY"
                />
                <StatCard
                    title="Threat Matrix Index"
                    value={metrics.criticalCount > 4 ? "CRITICAL" : (metrics.criticalCount > 0 ? "HIGH" : "ELEVATED")}
                    sub="Correlated Risk Exposure"
                    icon={Zap}
                    color={metrics.criticalCount > 4 ? "red" : "purple"}
                    badge="STATUS"
                />
            </div>

            {/* ── ADVANCED INTELLIGENCE FILTER TOOLBAR ── */}
            <div className="bg-[#0F172A]/90 backdrop-blur-xl p-4 rounded-2xl border border-white/[0.08] shadow-xl flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 text-slate-400 pr-2 border-r border-slate-800">
                        <Filter className="w-4 h-4 text-blue-400" />
                        <span className="text-[11px] font-black uppercase tracking-wider text-slate-200">Filters</span>
                        {activeFilterCount > 0 && (
                            <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
                                {activeFilterCount}
                            </span>
                        )}
                    </div>

                    {/* Search Input */}
                    <div className="flex items-center gap-2 bg-[#1E293B]/60 border border-white/[0.06] rounded-xl px-3 py-1.5 focus-within:border-blue-500/50">
                        <Search className="w-3.5 h-3.5 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search CVE, attacker, victim, asset..."
                            className="bg-transparent text-xs font-medium text-slate-200 outline-none w-56 placeholder:text-slate-500"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && { fetchInsights, fetchHeadlines }}
                        />
                        {searchQuery && (
                            <button onClick={() => setSearchQuery("")} className="text-slate-500 hover:text-slate-300">
                                <X className="w-3 h-3" />
                            </button>
                        )}
                    </div>

                    {/* Severity Filter */}
                    <div className="relative">
                        <select
                            value={selectedSeverity}
                            onChange={(e) => setSelectedSeverity(e.target.value)}
                            className="bg-[#1E293B]/60 border border-white/[0.06] text-slate-200 text-xs font-bold px-3 py-2 rounded-xl outline-none focus:border-blue-500/50 cursor-pointer appearance-none pr-8"
                        >
                            <option value="all">All Severities</option>
                            <option value="CRITICAL">Critical Severity</option>
                            <option value="HIGH">High Severity</option>
                            <option value="MEDIUM">Medium Severity</option>
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>

                    {/* Vector Category Filter */}
                    <div className="relative">
                        <select
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value)}
                            className="bg-[#1E293B]/60 border border-white/[0.06] text-slate-200 text-xs font-bold px-3 py-2 rounded-xl outline-none focus:border-blue-500/50 cursor-pointer appearance-none pr-8"
                        >
                            <option value="all">All Vector Categories</option>
                            <option value="PRE-BREACH">Pre-Breach Reconnaissance</option>
                            <option value="COMMAND & CONTROL">Command &amp; Control</option>
                            <option value="ADVISORY">CTI / CISA Advisories</option>
                            <option value="DARK WEB">Dark Web Signals</option>
                            <option value="SUSPICIOUS">Suspicious Activity</option>
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                </div>

                {/* Filter Actions */}
                <div className="flex items-center gap-2">
                    {activeFilterCount > 0 && (
                        <button
                            onClick={clearFilters}
                            className="px-3.5 py-2 text-slate-400 hover:text-slate-200 text-xs font-bold uppercase tracking-wider transition-colors"
                        >
                            Reset
                        </button>
                    )}
                    <button
                        onClick={() => { fetchInsights(); fetchHeadlines(1); }}
                        disabled={loading}
                        className="px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all shadow-[0_0_20px_rgba(59,130,246,0.3)] active:scale-95 disabled:opacity-50"
                    >
                        <Activity className="w-3.5 h-3.5" /> Apply Filters
                    </button>
                </div>
            </div>

            {/* ── WORKSPACE SPLIT (12 COLUMNS) ── */}
            {loading && insights.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-40 gap-6 bg-[#0F172A]/40 rounded-3xl border border-white/[0.04]">
                    <div className="relative">
                        <div className="w-16 h-16 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
                        <ShieldAlert className="w-7 h-7 text-red-500 absolute inset-0 m-auto animate-pulse" />
                    </div>
                    <p className="text-xs font-black uppercase tracking-[0.3em] text-slate-400 animate-pulse">
                        Correlating Heuristic Patterns &amp; Interrogating Global Feeds...
                    </p>
                </div>
            ) : error ? (
                <div className="p-12 rounded-3xl bg-red-500/5 border border-red-500/20 text-red-400 text-center flex flex-col items-center gap-4">
                    <AlertCircle className="w-12 h-12 opacity-50" />
                    <p className="font-black uppercase tracking-widest text-xs">{error}</p>
                    <button onClick={fetchInsights} className="text-xs font-bold underline uppercase tracking-widest hover:text-red-300">
                        Retry Connection
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">

                    {/* ── LEFT COLUMN: CORRELATED HEURISTIC PATTERNS (5 cols) ── */}
                    <div className="xl:col-span-5 space-y-4">
                        <div className="flex items-center justify-between px-1">
                            <div className="flex items-center gap-2">
                                <ShieldCheck className="w-5 h-5 text-emerald-400" />
                                <h2 className="text-sm font-black text-slate-100 uppercase tracking-widest">
                                    Correlated Heuristic Patterns
                                </h2>
                            </div>
                            <span className="text-[10px] text-slate-400 font-mono font-bold bg-[#1E293B] px-2 py-0.5 rounded border border-white/[0.06]">
                                {insights.length} Correlated Vectors
                            </span>
                        </div>

                        <div className="space-y-3.5 max-h-[850px] overflow-y-auto pr-1">
                            {insights.length === 0 ? (
                                <div className="p-12 bg-[#0F172A]/80 rounded-3xl border border-dashed border-white/[0.08] text-center space-y-2">
                                    <ShieldCheck className="w-8 h-8 text-emerald-500 mx-auto opacity-40" />
                                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                                        No active compromised vectors matching filter
                                    </p>
                                </div>
                            ) : (
                                insights.map((item, idx) => {
                                    const sev = (item.severity || "HIGH").toUpperCase();
                                    const sevStyle = SEVERITY_THEMES[sev] || SEVERITY_THEMES.HIGH;

                                    return (
                                        <div
                                            key={item._id || idx}
                                            onClick={() => setInspectVector(item)}
                                            className={`p-5 rounded-2xl border transition-all duration-200 cursor-pointer group bg-[#0F172A]/90 hover:bg-[#1E293B]/80 hover:border-white/[0.15] relative overflow-hidden shadow-lg ${
                                                sev === "CRITICAL" ? "border-red-500/30" : "border-white/[0.06]"
                                            }`}
                                        >
                                            {/* Glowing ambient light */}
                                            <div className={`absolute top-0 right-0 w-32 h-32 blur-[60px] opacity-10 pointer-events-none ${sevStyle.glow}`} />

                                            {/* Left accent stripe */}
                                            {sev === "CRITICAL" && (
                                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)]" />
                                            )}

                                            {/* Top Row: Vector Type & Severity Badge */}
                                            <div className="flex justify-between items-start gap-2 mb-2">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="text-xs font-black text-blue-400 uppercase tracking-tight flex items-center gap-1.5">
                                                        <span className={`w-2 h-2 rounded-full ${sevStyle.dot}`} />
                                                        {item.type}
                                                    </span>
                                                    {item.cve && item.cve !== "N/A" && (
                                                        <span className="px-1.5 py-0.5 rounded text-[9px] font-mono font-bold bg-purple-500/10 text-purple-400 border border-purple-500/20">
                                                            {item.cve}
                                                        </span>
                                                    )}
                                                </div>

                                                <span className={`text-[9px] font-black uppercase px-2.5 py-0.5 rounded-full border ${sevStyle.badge}`}>
                                                    {sev}
                                                </span>
                                            </div>

                                            {/* Description */}
                                            <p className="text-slate-200 text-xs sm:text-sm font-medium leading-relaxed mb-3 group-hover:text-white">
                                                {item.description}
                                            </p>

                                            {/* Metadata Chips: Mitre, Target Asset, Hits in Env */}
                                            <div className="flex flex-wrap items-center gap-2 mb-3">
                                                {item.targetAsset && (
                                                    <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-[#1E293B] border border-white/[0.06] text-slate-300 flex items-center gap-1">
                                                        <Server className="w-3 h-3 text-slate-400" /> {item.targetAsset}
                                                    </span>
                                                )}
                                                {item.mitreTechnique && (
                                                    <span className="px-2 py-0.5 rounded-md text-[10px] font-bold font-mono bg-blue-500/10 border border-blue-500/20 text-blue-300">
                                                        {item.mitreTechnique}
                                                    </span>
                                                )}
                                                {item.hitsInEnv > 0 && (
                                                    <span className="px-2 py-0.5 rounded-md text-[10px] font-mono font-bold bg-orange-500/10 border border-orange-500/20 text-orange-400">
                                                        {item.hitsInEnv} Hits in Env
                                                    </span>
                                                )}
                                            </div>

                                            {/* Bottom Timestamp and Action */}
                                            <div className="flex items-center justify-between text-[11px] text-slate-400 border-t border-white/[0.04] pt-2.5 font-mono">
                                                <span className="flex items-center gap-1.5">
                                                    <Clock className="w-3 h-3 text-slate-500" />
                                                    {formatTimestamp(item.timestamp)}
                                                </span>
                                                <span className="text-blue-400 group-hover:text-blue-300 flex items-center gap-1 font-bold text-xs uppercase font-sans">
                                                    Inspect Vector <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* ── RIGHT COLUMN: GLOBAL INTELLIGENCE & DARKWEB FEED (7 cols) ── */}
                    <div className="xl:col-span-7 space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                            <div className="flex items-center gap-2">
                                <Globe className="w-5 h-5 text-blue-400 animate-pulse" />
                                <h2 className="text-sm font-black text-slate-100 uppercase tracking-widest">
                                    Global Intelligence &amp; Darkweb Feed
                                </h2>
                            </div>

                            <div className="flex items-center gap-2 px-3 py-1 bg-red-500/10 rounded-full border border-red-500/20">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-ping" />
                                <span className="text-[9px] font-black text-red-400 uppercase tracking-widest">
                                    {headlinesTotal ? `${headlinesTotal.toLocaleString()} Ingested Articles` : "Live OSINT"}
                                </span>
                            </div>
                        </div>

                        {/* Headlines Grid (2 cols) */}
                        {headlinesLoading ? (
                            <div className="py-24 flex flex-col items-center justify-center gap-3 bg-[#0F172A]/60 rounded-3xl border border-white/[0.04]">
                                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                <span className="text-xs font-mono text-slate-400 uppercase tracking-widest">Loading Verified Threat Intel...</span>
                            </div>
                        ) : headlines.length === 0 ? (
                            <div className="p-16 bg-[#0F172A]/80 rounded-3xl border border-dashed border-white/[0.08] text-center">
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">No articles matching current search parameters</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {headlines.map((h, idx) => {
                                    const sourceStyle = SOURCE_COLORS[h.source] || SOURCE_COLORS.Default;

                                    return (
                                        <div
                                            key={h._id || idx}
                                            className="bg-[#0F172A]/90 p-5 rounded-2xl border border-white/[0.06] hover:border-white/[0.15] hover:bg-[#1E293B]/60 transition-all flex flex-col justify-between group shadow-lg"
                                        >
                                            <div className="space-y-3">
                                                <div className="flex justify-between items-start gap-2">
                                                    <div className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg border font-mono ${sourceStyle}`}>
                                                        <Rss className="w-3 h-3" />
                                                        <span className="text-[9px] font-black uppercase tracking-wider">{h.source}</span>
                                                    </div>

                                                    {h.exposure && (
                                                        <div className="px-2 py-0.5 bg-red-500/20 rounded-lg border border-red-500/30 flex items-center gap-1">
                                                            <AlertTriangle className="w-2.5 h-2.5 text-red-500" />
                                                            <span className="text-[8px] font-black text-red-400 uppercase">Credential Leak</span>
                                                        </div>
                                                    )}
                                                </div>

                                                <h3 className="text-xs sm:text-sm font-bold text-slate-200 leading-snug group-hover:text-blue-400 transition-colors">
                                                    {h.title}
                                                </h3>
                                            </div>

                                            <div className="mt-4 flex items-center justify-between pt-2.5 border-t border-white/[0.04]">
                                                <span className="text-[10px] font-mono text-slate-400 flex items-center gap-1">
                                                    <Clock className="w-3 h-3 text-slate-500" />
                                                    {formatTimestamp(h.date)}
                                                </span>
                                                <a
                                                    href={h.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="p-2 bg-[#1E293B] hover:bg-blue-600 rounded-lg text-slate-300 hover:text-white transition-all flex items-center gap-1 text-[10px] font-bold"
                                                    title="Verify Source Article"
                                                >
                                                    <span>Verify</span>
                                                    <ExternalLink className="w-3 h-3" />
                                                </a>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Pagination Bar */}
                        {headlinesTotal > 14 && (
                            <div className="p-4 bg-[#0F172A]/90 border border-white/[0.06] rounded-2xl flex items-center justify-between text-xs font-mono text-slate-400">
                                <span>Showing page <strong>{headlinesPage}</strong> of <strong>{Math.ceil(headlinesTotal / 14)}</strong> ({headlinesTotal.toLocaleString()} total items)</span>
                                <div className="flex items-center gap-2">
                                    <button
                                        disabled={headlinesPage <= 1 || headlinesLoading}
                                        onClick={() => fetchHeadlines(headlinesPage - 1)}
                                        className="px-3 py-1.5 bg-[#1E293B] hover:bg-slate-700 rounded-xl border border-white/[0.08] disabled:opacity-40 transition-colors"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        disabled={headlinesPage >= Math.ceil(headlinesTotal / 14) || headlinesLoading}
                                        onClick={() => fetchHeadlines(headlinesPage + 1)}
                                        className="px-3 py-1.5 bg-[#1E293B] hover:bg-slate-700 rounded-xl border border-white/[0.08] disabled:opacity-40 transition-colors"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            )}

            {/* ── THREAT VECTOR DEEP-DIVE INSPECTION MODAL ── */}
            {inspectVector && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
                    <div className="bg-[#0F172A] border border-blue-500/30 rounded-3xl p-6 max-w-xl w-full shadow-2xl space-y-5">
                        <div className="flex items-start justify-between border-b border-white/[0.08] pb-4">
                            <div className="flex items-center gap-3">
                                <div className="p-3 rounded-2xl bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                    <Crosshair className="w-6 h-6" />
                                </div>
                                <div>
                                    <span className="text-[10px] font-black uppercase text-blue-400 tracking-widest">
                                        Heuristic Threat Vector Inspection
                                    </span>
                                    <h3 className="text-base font-black text-slate-100 uppercase tracking-tight">
                                        {inspectVector.type}
                                    </h3>
                                </div>
                            </div>
                            <button
                                onClick={() => setInspectVector(null)}
                                className="text-slate-400 hover:text-slate-200 p-1"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Vector Details Body */}
                        <div className="space-y-4 text-xs font-medium">
                            <div className="bg-[#1E293B]/60 p-4 rounded-2xl border border-white/[0.06] space-y-1.5">
                                <span className="text-[9px] font-black uppercase text-slate-400 tracking-widest">Correlated Threat Summary</span>
                                <p className="text-sm font-bold text-slate-100 leading-relaxed">
                                    {inspectVector.description}
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-3 font-mono">
                                <div className="bg-[#1E293B]/40 p-3 rounded-xl border border-white/[0.04]">
                                    <span className="text-[10px] text-slate-500 block uppercase font-bold">Severity Level</span>
                                    <span className="text-sm font-black text-red-400">{inspectVector.severity}</span>
                                </div>
                                <div className="bg-[#1E293B]/40 p-3 rounded-xl border border-white/[0.04]">
                                    <span className="text-[10px] text-slate-500 block uppercase font-bold">Intelligence Source</span>
                                    <span className="text-sm font-bold text-slate-200">{inspectVector.source}</span>
                                </div>
                                <div className="bg-[#1E293B]/40 p-3 rounded-xl border border-white/[0.04]">
                                    <span className="text-[10px] text-slate-500 block uppercase font-bold">Targeted Asset</span>
                                    <span className="text-xs font-bold text-blue-400 truncate block">{inspectVector.targetAsset || "Perimeter Gateway"}</span>
                                </div>
                                <div className="bg-[#1E293B]/40 p-3 rounded-xl border border-white/[0.04]">
                                    <span className="text-[10px] text-slate-500 block uppercase font-bold">MITRE Technique</span>
                                    <span className="text-xs font-bold text-purple-400 truncate block">{inspectVector.mitreTechnique || "T1190 (Exploit Public-Facing App)"}</span>
                                </div>
                            </div>

                            {inspectVector.cve && (
                                <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-xl flex items-center justify-between font-mono">
                                    <span className="text-xs text-purple-300 font-bold">Associated Vulnerability:</span>
                                    <span className="text-xs font-black text-purple-400">{inspectVector.cve}</span>
                                </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap items-center justify-end gap-3 pt-3 border-t border-white/[0.08]">
                            <button
                                onClick={() => {
                                    setInspectVector(null);
                                    navigate("/threat-hunt");
                                }}
                                className="px-4 py-2 bg-[#1E293B] hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-black uppercase tracking-wider border border-white/[0.08] transition-all flex items-center gap-1.5"
                            >
                                <Compass className="w-3.5 h-3.5 text-blue-400" /> Launch Threat Hunt
                            </button>
                            <button
                                onClick={() => {
                                    setInspectVector(null);
                                    if (inspectVector.incidentId) navigate("/incidents");
                                    else navigate("/cases");
                                }}
                                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-1.5 shadow-lg shadow-blue-600/30 transition-all"
                            >
                                <Play className="w-3.5 h-3.5" /> Open Incident Room
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
